/**
 * i18n del embudo post-pago de membresía (CompleteRegistration + PendingApproval).
 * Solo dos idiomas: `es` (default) y `en` — coherente con la ruta:
 *   /portal/completar-perfil  → es
 *   /portal/complete-profile  → en
 *
 * Resolución del idioma:
 *   1) `?lang=en|es` en la URL (el helper `getPostPaymentSuccessPath` emite `lang=`
 *      en el `success_url` de Stripe, así que viaja end-to-end).
 *   2) Pathname canónico: `/portal/complete-profile` ⇒ `en`.
 *   3) Fallback: `navigator.language` empezando con `en` ⇒ `en`; resto ⇒ `es`.
 */

export type MembershipFlowLanguage = "en" | "es";

export function resolveMembershipFlowLanguage(
  searchParams: URLSearchParams,
  pathname: string,
): MembershipFlowLanguage {
  const raw = (
    searchParams.get("lang") ||
    searchParams.get("language") ||
    ""
  )
    .trim()
    .toLowerCase();
  if (raw === "en" || raw.startsWith("en-")) return "en";
  if (raw === "es" || raw.startsWith("es-")) return "es";
  if (pathname.includes("/complete-profile")) return "en";
  if (pathname.includes("/completar-perfil") || pathname.includes("/completar-registro"))
    return "es";
  if (typeof navigator !== "undefined") {
    const nav = navigator.language?.toLowerCase() || "";
    if (nav.startsWith("en")) return "en";
  }
  return "es";
}

export interface CompleteRegistrationStrings {
  seoTitle: string;
  seoDescription: (brand: string) => string;
  cardTitle: string;
  cardDescriptionVip: string;
  cardDescriptionBasicOrStandard: string;
  /** Free-join: registro gratis → perfil → link grupo free. */
  cardDescriptionFreeJoin: string;
  aliasLabel: string;
  aliasPlaceholder: string;
  aliasHelp: string;
  telegramLabel: string;
  telegramPlaceholder: string;
  telegramHelp: string;
  telegramHelpFreeJoin: string;
  birthDateLabel: string;
  birthDateHelp: (minAge: number) => string;
  termsAccept: string;
  termsLink: string;
  privacyLink: string;
  termsAfterLink: (minAge: number) => string;
  termsRequired: string;
  errorSaveProfile: string;
  hintAfterSaveVip: string;
  hintAfterSaveBasicOrStandard: string;
  hintAfterSaveFreeJoin: string;
  submitIdle: string;
  submitSaving: string;
  submitFreeJoin: string;
  /** Mini process strip on profile form (before In review). */
  processStepPaid: string;
  processStepProfile: string;
  processStepInReview: string;
  processStepAccess: string;
}

export interface PendingApprovalStrings {
  seoTitle: string;
  seoDescription: (brand: string) => string;
  greeting: (name: string) => string;
  descriptionAfterCheckout: (brand: string) => string;
  descriptionAccessRequest: (brand: string) => string;
  almostThereTitle: string;
  almostThereBody: (email: string) => string;
  whatsNextTitle: string;
  whatsNextItems: (isAccessRequest: boolean) => string[];
  /** 3-step process strip: completed → in review (current) → access ready */
  processStepDone: string;
  processStepInReview: string;
  processStepAccess: string;
  /** Primary status CTA — user feels they are mid-process (not a dead end). */
  inReviewButton: string;
  inReviewHint: string;
  verifyingLabel: string;
  lastCheckedLabel: (timeLabel: string) => string;
  liveLabel: string;
  verifyNowIdle: string;
  verifyNowBusy: string;
  notifyTeamIdle: string;
  notifyTeamBusy: string;
  notifyTeamCooldown: (minutes: number) => string;
  signOut: string;
  approvedTitle: string;
  approvedSubtitle: (brand: string) => string;
  approvedRedirect: (seconds: number) => string;
  autoRefresh: (seconds: number) => string;
  contactLink: string;
  manualTelegramAssignment: string;
  toastWaitTitle: string;
  toastWaitDescription: (minutes: number) => string;
  toastBannedTitle: string;
  toastBannedDescription: string;
  toastSentTitle: string;
  toastSentDescription: string;
  toastErrorTitle: string;
  toastErrorDescription: string;
  timeLocale: string;
}

const ES_COMPLETE: CompleteRegistrationStrings = {
  seoTitle: "Completá tu perfil",
  seoDescription: (brand) =>
    `Completá tu perfil para acceder a tu membresía de ${brand}.`,
  cardTitle: "¡Último paso antes de la revisión!",
  cardDescriptionVip:
    "Tu pago VIP ya está. Completá este perfil y pasás al paso «En revisión» (In review). Ahí el equipo prepara tu invitación privada de Telegram. Sin este paso no se entrega el link.",
  cardDescriptionBasicOrStandard:
    "Completá estos datos y pasás a «En revisión». El equipo valida tu acceso y te envía el link cuando corresponda.",
  cardDescriptionFreeJoin:
    "Completá tu perfil gratis. En el siguiente paso te muestro el link del grupo free de la comunidad.",
  aliasLabel: "Alias o apodo",
  aliasPlaceholder: "¿Cómo te gustaría que te llame?",
  aliasHelp: "Entre 3 y 30 caracteres",
  telegramLabel: "Telegram",
  telegramPlaceholder: "@usuario o +54911...",
  telegramHelp: "Necesario para asignarte el grupo privado.",
  telegramHelpFreeJoin: "Tu usuario de Telegram — así te reconozco en la comunidad free.",
  birthDateLabel: "Fecha de nacimiento",
  birthDateHelp: (minAge) => `+${minAge} años`,
  termsAccept: "Acepto los",
  termsLink: "Términos y Condiciones",
  privacyLink: "Política de privacidad",
  termsAfterLink: (minAge) => ` y confirmo que tengo al menos ${minAge} años.`,
  termsRequired: "Debés aceptar los Términos y Condiciones para continuar.",
  errorSaveProfile: "No se pudo guardar el perfil.",
  hintAfterSaveVip:
    "Al guardar entras a «En revisión». El equipo te enviará la invitación de Telegram (puede demorar unas horas). Revisá el email y la página de estado.",
  hintAfterSaveBasicOrStandard:
    "Al guardar entras a «En revisión». El equipo validará tu cuenta y te enviará el link cuando corresponda.",
  hintAfterSaveFreeJoin:
    "Al guardar vas a ver el link del grupo free. Sin pago — es el acceso de la comunidad.",
  submitIdle: "Guardar y enviar a revisión",
  submitSaving: "Guardando...",
  submitFreeJoin: "Guardar y ver el grupo free",
  processStepPaid: "Pago",
  processStepProfile: "Perfil",
  processStepInReview: "En revisión",
  processStepAccess: "Acceso",
};

const EN_COMPLETE: CompleteRegistrationStrings = {
  seoTitle: "Complete your profile",
  seoDescription: (brand) =>
    `Complete your profile to unlock your ${brand} membership.`,
  cardTitle: "One last step before review!",
  cardDescriptionVip:
    "Your VIP payment is registered. Finish this profile and you move to «In review». The team then prepares your private Telegram invite. Without this step you will not receive the link.",
  cardDescriptionBasicOrStandard:
    "Fill in these details and you move to «In review». The team validates access and sends your link when ready.",
  cardDescriptionFreeJoin:
    "Finish your free profile. Next screen shows your free community group link.",
  aliasLabel: "Nickname",
  aliasPlaceholder: "What should we call you?",
  aliasHelp: "Between 3 and 30 characters",
  telegramLabel: "Telegram",
  telegramPlaceholder: "@username or +1 415...",
  telegramHelp: "Needed so we can add you to the private group.",
  telegramHelpFreeJoin: "Your Telegram handle — so we recognize you in the free community.",
  birthDateLabel: "Date of birth",
  birthDateHelp: (minAge) => `${minAge}+ only`,
  termsAccept: "I accept the",
  termsLink: "Terms & Conditions",
  privacyLink: "Privacy Policy",
  termsAfterLink: (minAge) => ` and confirm I am at least ${minAge} years old.`,
  termsRequired: "You must accept the Terms & Conditions to continue.",
  errorSaveProfile: "We couldn't save your profile.",
  hintAfterSaveVip:
    "After you save, you enter «In review». The team will send your private Telegram invite (it can take a few hours). Check email and the status page.",
  hintAfterSaveBasicOrStandard:
    "After you save, you enter «In review». The team will validate your account and send your access link when ready.",
  hintAfterSaveFreeJoin:
    "After you save, you'll see the free group link. No payment — community access only.",
  submitIdle: "Save and submit for review",
  submitSaving: "Saving...",
  submitFreeJoin: "Save and see free group",
  processStepPaid: "Paid",
  processStepProfile: "Profile",
  processStepInReview: "In review",
  processStepAccess: "Access",
};

const ES_PENDING: PendingApprovalStrings = {
  seoTitle: "Tu cuenta está en revisión",
  seoDescription: (brand) =>
    `Tu acceso a la comunidad de ${brand} está siendo revisado.`,
  greeting: (name) => `Hola ${name} 👋`,
  descriptionAfterCheckout: (brand) =>
    `¡Gracias de corazón por sumarte! 💜 Tu pago VIP ya está. Completaste el registro en Clerk — ahora ${brand} prepara tu invitación privada de Telegram. Sin el registro no se envía el link. Estás dentro; solo falta la asignación del equipo.`,
  descriptionAccessRequest: (brand) =>
    `¡Qué bueno tenerte acá! 💜 ${brand} está revisando tu cuenta con cariño y te va a dar acceso muy pronto. No tenés que hacer nada más.`,
  almostThereTitle: "Tranqui, ya casi 💛",
  almostThereBody: (email) =>
    `Revisamos cada cuenta a mano, con cariño, para que todo salga perfecto. A veces toma unas horas según el momento del día — pero te estamos esperando. Te escribimos a ${email} apenas esté tu link listo. Podés cerrar esta página tranquilo/a.`,
  whatsNextTitle: "Tu proceso — paso a paso",
  whatsNextItems: (isAccessRequest) => [
    isAccessRequest
      ? "Solicitud enviada — listo ✓"
      : "Pago y registro completados — listo ✓",
    "En revisión — el equipo prepara tu acceso (estás acá)",
    "Te preparamos tu link privado personal (Telegram)",
    "Esta página se actualiza sola y te lleva al acceso + email 💌",
  ],
  processStepDone: "Listo",
  processStepInReview: "En revisión",
  processStepAccess: "Tu acceso",
  inReviewButton: "En revisión",
  inReviewHint:
    "Paso actual: tu cuenta está en la cola del equipo. No tenés que hacer nada más — el proceso sigue solo.",
  verifyingLabel: "Verificando...",
  lastCheckedLabel: (timeLabel) => `Última verificación: ${timeLabel}`,
  liveLabel: "Verificación activa",
  verifyNowIdle: "Verificar ahora",
  verifyNowBusy: "Verificando...",
  notifyTeamIdle: "Avisar al equipo que estoy esperando",
  notifyTeamBusy: "Avisando al equipo...",
  notifyTeamCooldown: (minutes) => `Esperá ${minutes} min`,
  signOut: "Cerrar sesión",
  approvedTitle: "¡Aprobado!",
  approvedSubtitle: (brand) =>
    `Bienvenido a la comunidad exclusiva de ${brand}.`,
  approvedRedirect: (seconds) => `Redirigiendo en ${seconds}s...`,
  autoRefresh: (seconds) => `Auto-actualiza cada ${seconds}s`,
  contactLink: "Contacto",
  manualTelegramAssignment: "Asignación manual del grupo privado",
  toastWaitTitle: "Esperá un poco",
  toastWaitDescription: (minutes) =>
    `Ya avisaste al equipo. Volvé a intentar en ${minutes} minuto${minutes !== 1 ? "s" : ""}.`,
  toastBannedTitle: "Cuenta bloqueada",
  toastBannedDescription:
    "Excediste el límite de notificaciones. Tu cuenta fue bloqueada por abuso.",
  toastSentTitle: "Aviso enviado",
  toastSentDescription:
    "El equipo recibió tu mensaje. Te aprobarán en cuanto sea posible.",
  toastErrorTitle: "Error",
  toastErrorDescription: "No se pudo enviar el aviso.",
  timeLocale: "es-AR",
};

const EN_PENDING: PendingApprovalStrings = {
  seoTitle: "Your account is under review",
  seoDescription: (brand) =>
    `Your access to ${brand}'s community is being reviewed.`,
  greeting: (name) => `Hi ${name} 👋`,
  descriptionAfterCheckout: (brand) =>
    `Thank you so much for joining! 💜 Your VIP payment is registered and you finished Clerk registration. ${brand} is preparing your private Telegram invite. Without registration the invite is not sent. You're in — just waiting on the team.`,
  descriptionAccessRequest: (brand) =>
    `Great to have you here! 💜 ${brand} is reviewing your account with care and will get you access very soon. Nothing else for you to do.`,
  almostThereTitle: "Hang tight, almost there 💛",
  almostThereBody: (email) =>
    `We review every account by hand to make sure everything's perfect. It can take a few hours depending on the time of day — but we're on it. We'll email you at ${email} as soon as your link is ready. Feel free to close this page.`,
  whatsNextTitle: "Your process — step by step",
  whatsNextItems: (isAccessRequest) => [
    isAccessRequest
      ? "Request submitted — done ✓"
      : "Payment and registration complete — done ✓",
    "In review — the team is preparing your access (you are here)",
    "We prepare your personal private link (Telegram)",
    "This page updates itself and takes you to access + email 💌",
  ],
  processStepDone: "Done",
  processStepInReview: "In review",
  processStepAccess: "Your access",
  inReviewButton: "In review",
  inReviewHint:
    "Current step: your account is in the team queue. Nothing else for you to do — the process continues on its own.",
  verifyingLabel: "Checking...",
  lastCheckedLabel: (timeLabel) => `Last check: ${timeLabel}`,
  liveLabel: "Live check",
  verifyNowIdle: "Check now",
  verifyNowBusy: "Checking...",
  notifyTeamIdle: "Let the team know I'm waiting",
  notifyTeamBusy: "Notifying the team...",
  notifyTeamCooldown: (minutes) => `Wait ${minutes} min`,
  signOut: "Sign out",
  approvedTitle: "Approved!",
  approvedSubtitle: (brand) => `Welcome to ${brand}'s exclusive community.`,
  approvedRedirect: (seconds) => `Redirecting in ${seconds}s...`,
  autoRefresh: (seconds) => `Auto-refreshes every ${seconds}s`,
  contactLink: "Contact",
  manualTelegramAssignment: "Private group is assigned manually",
  toastWaitTitle: "Hold on a sec",
  toastWaitDescription: (minutes) =>
    `You already pinged the team. Try again in ${minutes} minute${minutes !== 1 ? "s" : ""}.`,
  toastBannedTitle: "Account blocked",
  toastBannedDescription:
    "You exceeded the notification limit. Your account was blocked for abuse.",
  toastSentTitle: "Notification sent",
  toastSentDescription:
    "The team got your message. They'll approve you as soon as possible.",
  toastErrorTitle: "Error",
  toastErrorDescription: "Couldn't send the notification.",
  timeLocale: "en-US",
};

export function getCompleteRegistrationStrings(
  lang: MembershipFlowLanguage,
): CompleteRegistrationStrings {
  return lang === "en" ? EN_COMPLETE : ES_COMPLETE;
}

export function getPendingApprovalStrings(
  lang: MembershipFlowLanguage,
): PendingApprovalStrings {
  return lang === "en" ? EN_PENDING : ES_PENDING;
}

/** Validation errors localizadas — usadas por `validateRegistrationData`. */
export const VALIDATION_MESSAGES = {
  es: {
    aliasLength: "El alias debe tener entre 3 y 30 caracteres.",
    telegramLength: "El usuario de Telegram debe tener entre 3 y 50 caracteres.",
    birthDateMissing: "Ingresá tu fecha de nacimiento.",
    minAge: (minAge: number) =>
      `Debes tener al menos ${minAge} años para continuar.`,
  },
  en: {
    aliasLength: "Nickname must be between 3 and 30 characters.",
    telegramLength: "Telegram handle must be between 3 and 50 characters.",
    birthDateMissing: "Please enter your date of birth.",
    minAge: (minAge: number) =>
      `You must be at least ${minAge} years old to continue.`,
  },
} as const;
